import React, { ReactNode } from 'react';
import {
  Box,
  Flex,
  HStack,
  Text,
  Avatar,
  useColorModeValue,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from '@chakra-ui/react';
import { FiMenu, FiBell, FiSettings } from 'react-icons/fi';
import Sidebar from 'components/sidebar/Sidebar';

interface MainLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, title, subtitle }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bg = useColorModeValue('background.100', 'background.900');
  const headerBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.200', 'navy.700');
  const textColor = useColorModeValue('secondaryGray.700', 'white');

  return (
    <Flex h="100vh" bg={bg}>
      {/* Desktop Sidebar */}
      <Box display={{ base: 'none', lg: 'block' }}>
        <Sidebar />
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody p="0">
            <Sidebar onClose={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Main Content */}
      <Flex
        direction="column"
        flex="1"
        ml={{ base: '0', lg: '280px' }}
        h="full"
        overflow="hidden"
      >
        {/* Header */}
        <Flex
          as="header"
          align="center"
          justify="space-between"
          w="full"
          h="80px"
          bg={headerBg}
          borderBottom="1px solid"
          borderColor={borderColor}
          px="24px"
          boxShadow="0px 1px 3px rgba(0, 0, 0, 0.04)"
          zIndex="docked"
        >
          {/* Left Side */}
          <HStack spacing="16px">
            <IconButton
              aria-label="Open menu"
              icon={<FiMenu />}
              variant="ghost"
              display={{ base: 'flex', lg: 'none' }}
              onClick={onOpen}
            />
            <Box>
              {title && (
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  {title}
                </Text>
              )}
              {subtitle && (
                <Text fontSize="sm" color="secondaryGray.500" mt="-2px">
                  {subtitle}
                </Text>
              )}
            </Box>
          </HStack>

          {/* Right Side */}
          <HStack spacing="12px">
            <IconButton
              aria-label="Notifications"
              icon={<FiBell />}
              variant="ghost"
              position="relative"
            >
              {/* Notification dot */}
              <Box
                position="absolute"
                top="8px"
                right="8px"
                w="8px"
                h="8px"
                bg="urgent.500"
                borderRadius="full"
                border="2px solid"
                borderColor={headerBg}
              />
            </IconButton>
            
            <IconButton
              aria-label="Settings"
              icon={<FiSettings />}
              variant="ghost"
            />

            <Box h="32px" w="1px" bg={borderColor} />

            <HStack spacing="8px">
              <Avatar
                size="sm"
                name="Sarah Mitchell"
                src=""
                bg="brand.500"
                color="white"
              />
              <Box display={{ base: 'none', md: 'block' }}>
                <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                  Sarah Mitchell
                </Text>
                <Text fontSize="xs" color="secondaryGray.500" mt="-2px">
                  Project Manager
                </Text>
              </Box>
            </HStack>
          </HStack>
        </Flex>

        {/* Content Area */}
        <Box
          flex="1"
          overflow="auto"
          p="24px"
          css={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            },
          }}
        >
          {children}
        </Box>
      </Flex>
    </Flex>
  );
};

export default MainLayout; 